/**
 * Boolean UI preferences (e.g. "is the chat panel open?") persisted in
 * `localStorage`: the plain read/write helpers, and a reactive hook for the
 * ones a control in one package sets and a surface in another package reads.
 *
 * Guards `typeof window` so calls are safe
 * during SSR / node-env tests, and swallows storage errors (private mode,
 * quota, disabled storage) so a failed read/write never breaks the editor.
 *
 * Values are encoded as `'1'` / `'0'`. A missing key returns the caller's
 * `fallback`, which lets each caller pick its own default-open behavior.
 */
import { useCallback, useSyncExternalStore } from 'react';

export function readBoolPref(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}

export function writeBoolPref(key: string, value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* noop */
  }
}

/* -------------------------------------------------------------------------- */
/* Reactive preferences                                                        */
/* -------------------------------------------------------------------------- */

/**
 * One store per key, shared by every component that reads it. A preference set
 * in the host's chrome has to reach surfaces a plugin owns — the toolbar menu
 * and the transcript are in different packages and neither can import the
 * other — so the store lives here, in the client surface both already depend
 * on, rather than being threaded through props across that boundary.
 */
interface PrefStore {
  value: boolean;
  listeners: Set<() => void>;
}

const stores = new Map<string, PrefStore>();

function getStore(key: string, fallback: boolean): PrefStore {
  const existing = stores.get(key);
  if (existing) return existing;
  const store: PrefStore = { value: readBoolPref(key, fallback), listeners: new Set() };
  stores.set(key, store);
  // Cross-tab sync: the browser fires `storage` in *other* tabs. One listener
  // per key rather than per subscriber, so a transcript with hundreds of rows
  // reading the same preference doesn't install hundreds of listeners.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== null && event.key !== key) return;
      store.value = readBoolPref(key, fallback);
      for (const listener of store.listeners) listener();
    });
  }
  return store;
}

/**
 * Write a boolean preference and tell every reader in this tab. Imperative
 * twin of {@link useBoolPref}, for callers that aren't rendering (and for
 * tests and stories that need to put a surface in a given mode).
 */
export function setBoolPref(key: string, value: boolean): void {
  writeBoolPref(key, value);
  const store = stores.get(key);
  if (!store || store.value === value) return;
  store.value = value;
  for (const listener of store.listeners) listener();
}

/**
 * A boolean preference and its setter, kept in sync across every component
 * reading the same key — in this tab through a shared store, across tabs
 * through the browser's `storage` event.
 */
export function useBoolPref(key: string, fallback: boolean): [boolean, (value: boolean) => void] {
  const store = getStore(key, fallback);
  const value = useSyncExternalStore(
    useCallback(
      (listener: () => void) => {
        store.listeners.add(listener);
        return () => store.listeners.delete(listener);
      },
      [store],
    ),
    () => store.value,
    () => fallback,
  );
  const setValue = useCallback((next: boolean) => setBoolPref(key, next), [key]);
  return [value, setValue];
}

/**
 * How the agent transcript reads. Both are per-browser presentation
 * preferences: they change how a run is shown, never what it did.
 *
 * `humanReadableCommands` is on by default. A shell row that says what the
 * command did is the better default for reading a run; turning it off is for
 * people who want the literal argv on every row, and it also skips downloading
 * the bash parser the rewrite needs.
 */
export const TRANSCRIPT_PREFS = {
  humanReadableCommands: 'chat-human-readable-commands',
  turnMenu: 'chat-turn-menu',
} as const;

/** Shell rows read as what the command did, rather than as the raw command. */
export function useHumanReadableCommands(): [boolean, (value: boolean) => void] {
  return useBoolPref(TRANSCRIPT_PREFS.humanReadableCommands, true);
}

/**
 * Whether the turn rail beside the transcript is wanted. Independent of
 * whether there is room for it: a narrow window hides the rail either way.
 */
export function useTurnMenuVisible(): [boolean, (value: boolean) => void] {
  return useBoolPref(TRANSCRIPT_PREFS.turnMenu, true);
}

/* -------------------------------------------------------------------------- */
/* String-array preferences                                                    */
/* -------------------------------------------------------------------------- */

function readStringArrayPref(key: string, fallback: readonly string[]): string[] {
  if (typeof window === 'undefined') return [...fallback];
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return [...fallback];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === 'string')) {
      return [...fallback];
    }
    return parsed;
  } catch {
    return [...fallback];
  }
}

function writeStringArrayPref(key: string, value: readonly string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* noop */
  }
}

interface StringArrayPrefStore {
  value: string[];
  listeners: Set<() => void>;
}

const stringArrayStores = new Map<string, StringArrayPrefStore>();

function getStringArrayStore(key: string, fallback: readonly string[]): StringArrayPrefStore {
  const existing = stringArrayStores.get(key);
  if (existing) return existing;
  const store: StringArrayPrefStore = {
    value: readStringArrayPref(key, fallback),
    listeners: new Set(),
  };
  stringArrayStores.set(key, store);
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== null && event.key !== key) return;
      store.value = readStringArrayPref(key, fallback);
      for (const listener of store.listeners) listener();
    });
  }
  return store;
}

/** Imperative twin of {@link useStringArrayPref}. */
export function setStringArrayPref(key: string, value: readonly string[]): void {
  writeStringArrayPref(key, value);
  const store = stringArrayStores.get(key);
  if (!store) return;
  const next = [...value];
  if (store.value.length === next.length && store.value.every((item, i) => item === next[i])) {
    return;
  }
  store.value = next;
  for (const listener of store.listeners) listener();
}

export function useStringArrayPref(
  key: string,
  fallback: readonly string[],
): [string[], (value: readonly string[]) => void] {
  const store = getStringArrayStore(key, fallback);
  const value = useSyncExternalStore(
    useCallback(
      (listener: () => void) => {
        store.listeners.add(listener);
        return () => store.listeners.delete(listener);
      },
      [store],
    ),
    () => store.value,
    () => [...fallback],
  );
  const setValue = useCallback((next: readonly string[]) => setStringArrayPref(key, next), [key]);
  return [value, setValue];
}

/**
 * Which agent actions sit as icon buttons on the right rail. Unpinned
 * customizable actions are hidden entirely. Header chrome (Mark as / Share /
 * Launch again / Save as persona / Delete) is never pinable here. Starts
 * empty so the rail stays tool-focused until the viewer pins something.
 */
export const AGENT_RAIL_PREFS = {
  pinnedActions: 'agent-rail-pinned-actions',
} as const;

/** Keys that start pinned until the viewer customizes the rail. */
export const DEFAULT_PINNED_AGENT_ACTIONS = [] as const;

export function usePinnedActionsPref(): [string[], (value: readonly string[]) => void] {
  return useStringArrayPref(AGENT_RAIL_PREFS.pinnedActions, DEFAULT_PINNED_AGENT_ACTIONS);
}
